import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconLink2SlashOutline18 as Link2Off,
	IconUsersOutline18 as Users,
} from "nucleo-ui-outline-18"

import { IssueStatusIcon } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { FilterType } from "./filter-popover-items/types"
import type { SearchableFilterItem } from "@/components/filters"
import { useFilteredSearchItems } from "@/components/filters"
import { companyDomain } from "@/lib/companies"
import { getProjectIcon } from "@/lib/projects"
import { useCompanyOptions } from "@/lib/use-entity-options"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { IssuePriorityIcon } from "@/screens/app/issues/issue-priority-icon"
import {
	LabelDot,
	useLabelOptions,
} from "./filter-popover-items/label-filter-items"
import { ROADMAP_PRIORITIES } from "./filter-popover-items/priority-filter-items"
import { ROADMAP_SOURCE_OPTIONS } from "./filter-popover-items/source-filter-items"
import { ROADMAP_STATUSES } from "./filter-popover-items/status-filter-items"
import { FILTER_CATEGORIES } from "./roadmap-filter"
import { useRoadmapFilterState } from "./roadmap-filter-state"
import { isTeamSelected } from "./roadmap-team-groups"
import { useRoadmapTeams } from "./use-roadmap-teams"

const CATEGORY_ORDER: FilterType[] = [
	"Statuses",
	"Teams",
	"Projects",
	"Priorities",
	"Companies",
	"Sources",
	"Labels",
]

export function useRoadmapSearchableItems({
	onCategorySelect,
	query = "",
}: {
	onCategorySelect: (type: FilterType) => void
	/** What the user typed; companies are searched on the server with it. */
	query?: string
}): SearchableFilterItem<FilterType>[] {
	const [projects] = useQuery(queries.projects())
	const labelOptions = useLabelOptions()
	const teams = useRoadmapTeams()
	const filterState = useRoadmapFilterState()
	const { companies } = useCompanyOptions({
		query,
		selectedIds: filterState.companyIds,
	})

	return useMemo((): SearchableFilterItem<FilterType>[] => {
		const items: SearchableFilterItem<FilterType>[] = []

		FILTER_CATEGORIES.forEach((cat) => {
			items.push({
				id: `category-${cat.type}`,
				category: cat.type,
				categoryLabel: cat.label,
				label: cat.label,
				searchText: cat.label,
				Icon: cat.Icon,
				onSelect: () => onCategorySelect(cat.type),
				isSelected: false,
			})
		})

		ROADMAP_STATUSES.forEach(({ value, label }) => {
			items.push({
				id: `status-${value}`,
				category: "Statuses",
				categoryLabel: "Status",
				label,
				searchText: `Status ${label}`,
				onSelect: () => {
					if (filterState.statuses.includes(value)) {
						filterState.removeStatus(value)
					} else {
						filterState.addStatus(value)
					}
				},
				isSelected: filterState.statuses.includes(value),
				renderCustom: () => (
					<IssueStatusIcon
						status={value}
						className="h-3.5 w-3.5 shrink-0 text-secondary"
					/>
				),
			})
		})

		teams.forEach((team) => {
			items.push({
				id: `team-${team.id}`,
				category: "Teams",
				categoryLabel: "Team",
				label: team.name,
				searchText: `Team ${team.name}`,
				Icon: Users,
				onSelect: () => {
					filterState.toggleTeam(team)
				},
				isSelected: isTeamSelected(team, filterState.teamIds),
			})
		})

		items.push({
			id: "project-none",
			category: "Projects",
			categoryLabel: "Project",
			label: "No project",
			searchText: "Project No project",
			Icon: Link2Off,
			onSelect: () => {
				if (filterState.projectIds.includes(null)) {
					filterState.removeProject(null)
				} else {
					filterState.addProject(null)
				}
			},
			isSelected: filterState.projectIds.includes(null),
		})

		projects.forEach((project) => {
			items.push({
				id: `project-${project.id}`,
				category: "Projects",
				categoryLabel: "Project",
				label: project.name,
				searchText: `Project ${project.name} ${project.publicTitle ?? ""} ${project.description ?? ""}`,
				Icon: getProjectIcon(project.icon),
				iconColor: project.color ?? undefined,
				onSelect: () => {
					if (filterState.projectIds.includes(project.id)) {
						filterState.removeProject(project.id)
					} else {
						filterState.addProject(project.id)
					}
				},
				isSelected: filterState.projectIds.includes(project.id),
			})
		})

		ROADMAP_PRIORITIES.forEach((priority) => {
			items.push({
				id: `priority-${priority}`,
				category: "Priorities",
				categoryLabel: "Priority",
				label: priority,
				searchText: `Priority ${priority}`,
				onSelect: () => {
					if (filterState.priorities.includes(priority)) {
						filterState.removePriority(priority)
					} else {
						filterState.addPriority(priority)
					}
				},
				isSelected: filterState.priorities.includes(priority),
				renderCustom: () => (
					<IssuePriorityIcon
						priority={priority}
						className="h-4 w-4 shrink-0 text-secondary"
					/>
				),
			})
		})

		items.push({
			id: "company-none",
			category: "Companies",
			categoryLabel: "Company",
			label: "No company",
			searchText: "Company No company",
			Icon: Link2Off,
			onSelect: () => {
				if (filterState.companyIds.includes(null)) {
					filterState.removeCompany(null)
				} else {
					filterState.addCompany(null)
				}
			},
			isSelected: filterState.companyIds.includes(null),
		})

		companies.forEach((company) => {
			items.push({
				id: `company-${company.id}`,
				category: "Companies",
				categoryLabel: "Company",
				label: company.name,
				secondaryLabel: companyDomain(company) ?? undefined,
				searchText: `Company ${company.name} ${company.domain ?? ""}`,
				onSelect: () => {
					if (filterState.companyIds.includes(company.id)) {
						filterState.removeCompany(company.id)
					} else {
						filterState.addCompany(company.id)
					}
				},
				isSelected: filterState.companyIds.includes(company.id),
				renderCustom: () => (
					<CompanyLogo
						linearLogoUrl={company.logoUrl ?? undefined}
						brandfetchData={null}
						fallback={company.name}
						size="xs"
					/>
				),
			})
		})

		labelOptions.forEach((option) => {
			items.push({
				id: `label-${option.key}`,
				category: "Labels",
				categoryLabel: "Label",
				label: option.name,
				searchText: `Label ${option.name}`,
				onSelect: () => filterState.toggleLabels(option.ids),
				isSelected: option.ids.some((id) => filterState.labelIds.includes(id)),
				renderCustom: () => <LabelDot color={option.color} />,
			})
		})

		ROADMAP_SOURCE_OPTIONS.forEach((option) => {
			items.push({
				id: `source-${option.value}`,
				category: "Sources",
				categoryLabel: "Source",
				label: option.label,
				secondaryLabel: option.description,
				searchText: `Source ${option.label}`,
				onSelect: () => filterState.toggleSource(option.value),
				isSelected: filterState.sources.includes(option.value),
			})
		})

		return items
	}, [projects, companies, labelOptions, teams, filterState, onCategorySelect])
}

export function useFilteredRoadmapSearchItems(
	globalQuery: string,
	allItems: SearchableFilterItem<FilterType>[],
) {
	return useFilteredSearchItems(globalQuery, allItems, {
		categoryOrder: CATEGORY_ORDER,
	})
}
